import React, { useCallback, useEffect, useRef, useState } from 'react';
import axios from 'axios';
import translate from '../translate';
import { compose } from '../../services/utilityFunctions';
import withCSRF from '../withCSRF';
import Modal from './Modal';
import FormInput from '../forms/FormInput';
import AsyncSelector from '../forms/inputs/AsyncSelector';
import ScrollContainer from '../container/ScrollContainer';
import Notifications from '../../services/notifications';
import Loader from '../../services/loader';
import UserColumn from '../lists/columns/UserColumn';
import CustomCheckbox from '../forms/inputs/CustomCheckbox';
import cx from 'classnames';

const ORGANIZATION_HEADERS = {
  'Accept-Version': '1.0.0',
  'X-Gamification-API': 'Organization',
};

const FILTER_TYPE = {
  COUNTRY: 'country',
  CITY: 'city',
  SPONSOR_NUMBER: 'sponsorNumber',
};

const fetchSponsors = async () => {
  try {
    const url = '/api/user/distributors/roundme/list';
    const params = {
      index: 1,
    };
    const { data: response } = await axios.get(url, {
      params,
      headers: {
        'Accept-Version': '1.0.0',
        'X-Gamification-API': 'Organization',
      },
    });
    return response.total;
  } catch (e) {
    Notifications.showErrorFromRequest(e);
    return 0;
  }
};

const handleResponse = param => data => {
  const isCountryCode = param === FILTER_TYPE.COUNTRY;
  return isCountryCode
    ? data?.map(country => ({
        id: country?.code,
        text: country?.name,
      }))
    : data?.map(city => ({ id: city, text: city }));
};

function SelectSponsorModal(props) {
  const { t, _csrf } = props;
  const loader = useRef(new Loader('#sponsorList'));

  const [isLoading, setIsLoading] = useState(false);
  const [filters, setFilters] = useState({
    sponsorNumber: null,
    country: null,
    city: null,
  });

  const [possibleSponsors, setPossibleSponsors] = useState([]);
  const [selectedSponsor, setSelectedSponsor] = useState(null);
  const [metaInfoTotal, setMetaInfoTotal] = useState(null);
  const [page, setPage] = useState(1);
  const [isAvailable, setIsAvailable] = useState(false);
  useEffect(() => {
    fetchSponsors().then(total => {
      setIsAvailable(total > 0);
    });
  }, []);
  const handleChange = useCallback(
    field => value => {
      setFilters(prevFilters => {
        if (field === FILTER_TYPE.COUNTRY) {
          return { ...prevFilters, [field]: value, city: null };
        }
        return { ...prevFilters, [field]: value };
      });
    },
    [setFilters],
  );
  const fetchPossibleSponsors = async (
    filtersParam = {},
    pageNumber = 1,
    initSponsors = [],
  ) => {
    try {
      loader.current.show();
      const url = '/table/user/distributors/roundme/list';

      const params = {};
      if (filtersParam?.sponsorNumber) {
        params.userNumber = filtersParam.sponsorNumber;
      }
      if (filtersParam?.city) {
        params.city = filtersParam.city;
      }
      if (filtersParam?.country) {
        params.countryCode = filtersParam?.country;
      }
      const { data: response } = await axios.get(url, {
        params: {
          pagination: { perpage: 10, page: pageNumber },
          ...params,
        },
        headers: {
          'Accept-Version': '1.0.0',
          'X-Gamification-API': 'Organization',
        },
      });
      const { data, meta } = response;
      return {
        pageNumber,
        sponsorsList: [...initSponsors, ...data],
        pages: meta?.pages,
      };
    } catch (err) {
      Notifications.showErrorFromRequest(err, 'error_fetchProducts');
      return {
        pageNumber: 1,
        sponsorsList: [],
        pages: 1,
      };
    } finally {
      loader.current.hide();
    }
  };

  /** region Gets first batch of items for page 1 */
  useEffect(() => {
    (async () => {
      const { sponsorsList, pages } = await fetchPossibleSponsors(
        filters,
        1,
        [],
      );
      setMetaInfoTotal(pages);
      setPossibleSponsors(sponsorsList);
      setPage(1);
    })();
  }, [filters]);

  /**
   * When scroll container gets to the end, we get another batch of items
   * If page is not greater that total of pages
   * And then we update the allVariation state
   * @type {(function(): Promise<void>)|*}
   */
  const isFetchingRef = useRef(false);
  const handleReachedEnd = useCallback(async () => {
    if (isFetchingRef.current) return; // Exit if a fetch is in progress

    isFetchingRef.current = true; // Set fetching status to true

    if (!metaInfoTotal || page + 1 <= metaInfoTotal) {
      const { pageNumber, sponsorsList, pages } = await fetchPossibleSponsors(
        filters,
        page + 1,
        possibleSponsors,
      );
      setPage(pageNumber);
      setMetaInfoTotal(pages);
      setPossibleSponsors(sponsorsList);
    }

    isFetchingRef.current = false; // Reset fetching status after fetch is done
  }, [
    setPossibleSponsors,
    setPage,
    setMetaInfoTotal,
    filters,
    metaInfoTotal,
    page,
    possibleSponsors,
  ]);
  // endregion HANDLE SCROLLABLE PRODUCTS

  const handleSelectSponsor = useCallback(
    sponsorId =>
      setSelectedSponsor(prevId => (prevId === sponsorId ? null : sponsorId)),
    [setSelectedSponsor],
  );

  const handleSubmit = useCallback(async () => {
    try {
      setIsLoading(true);
      await axios.put(
        '/user/update/sponsor',
        { sponsoredBy: selectedSponsor },
        {
          headers: { ...ORGANIZATION_HEADERS, 'X-CSRF-Token': _csrf, _csrf },
        },
      );
      Notifications.showNotificationSuccess(
        t('success'),
        t('selectSponsor.modal.success'),
      );
      location.reload(true);
    } catch (err) {
      Notifications.showErrorFromRequest(err);
    } finally {
      setIsLoading(false);
    }
  }, [selectedSponsor]);

  const submitBtnCx = cx(
    'btn',
    'btn-dynamic',
    'choose-sponsor-btn',
    { 'm-loader': isLoading },
    { 'm-loader--right': isLoading },
    { 'm-loader--light': isLoading },
  );

  return (
    <>
      {isAvailable ? (
        <Modal id="selectSponsorModal" tabIndex={-1} escToClose={false}>
          <div
            className="modal-dialog modal-dialog-centered modal-lg"
            style={{ maxWidth: '40vw', overflowX: 'auto' }}
            role="document"
          >
            <div className="modal-content selectSponsorModal">
              <div className="selectSponsorModal-header">
                <h4 className="title">{t('selectSponsor.modal.title')}</h4>
                <p className="information">
                  {t('selectSponsor.modal.information')}
                </p>
              </div>
              <div className="selectSponsorModal-filters">
                <div className="selectSponsorModal-filters-layer1">
                  <FormInput
                    id="sponsorNumberInput"
                    name="sponsorNumberInput"
                    type="text"
                    label={t('selectSponsor.modal.sponsorNumber')}
                    labelClassName="sponsorNumber-label"
                    inputProps={{ autoComplete: 'off' }}
                    placeholder={t('selectSponsor.modal.sponsorNumber')}
                    value={filters.sponsorNumber}
                    onChange={handleChange('sponsorNumber')}
                    groupProps={{ className: 'sponsorNumber-wrapper' }}
                    containerClassName="sponsorNumber-container"
                  />
                </div>
                <div className="selectSponsorModal-filters-layer2">
                  <AsyncSelector
                    url="/table/user/distributors/country/list"
                    id="countryListSelector"
                    onChange={handleChange(FILTER_TYPE.COUNTRY)}
                    placeholder={t('selectSponsor.modal.country.placeholder')}
                    label={t('selectSponsor.modal.country')}
                    labelClassName="country-label"
                    groupProps={{
                      ignoreWrapper: true,
                      className: 'country-wrapper',
                    }}
                    value={filters?.country}
                    ignoreWrapper
                    pageSize={10}
                    allowClear
                    displayNameInSelector={item => item?.name}
                    dropdownParent="#selectSponsorModal"
                    headers={ORGANIZATION_HEADERS}
                    containerClassName="country-container"
                    onHandleResponse={handleResponse(FILTER_TYPE.COUNTRY)}
                  />
                  <AsyncSelector
                    key={filters.country || 'city'}
                    url="/table/user/distributors/city/list"
                    id="cityListSelector"
                    onChange={handleChange(FILTER_TYPE.CITY)}
                    placeholder={t('selectSponsor.modal.city.placeholder')}
                    label={t('selectSponsor.modal.city')}
                    labelClassName="city-label"
                    groupProps={{
                      ignoreWrapper: true,
                      className: 'city-wrapper',
                    }}
                    value={filters?.city}
                    ignoreWrapper
                    pageSize={10}
                    allowClear
                    displayNameInSelector={item => item}
                    dropdownParent="#selectSponsorModal"
                    headers={ORGANIZATION_HEADERS}
                    containerClassName="city-container"
                    onHandleResponse={handleResponse(FILTER_TYPE.CITY)}
                    query={{ countryCode: filters.country }}
                  />
                </div>
              </div>
              <div className="selectSponsorModal-body">
                <p className="sponsor-message">
                  {t('selectSponsor.modal.sponsors.in.area')}
                </p>
                <div id="sponsorList">
                  <div className="content-wrapper">
                    {possibleSponsors?.length > 0 ? (
                      <ScrollContainer
                        onReachedEnd={handleReachedEnd}
                        onReachedEndDelay={1500}
                        containerClass="sponsorList-wrapper"
                      >
                        {possibleSponsors?.map((user, index) => (
                          <div
                            className={`user-list-item ${
                              index % 2 === 0 ? 'even' : 'odd'
                            }`}
                            key={`${user._id}_${index}`}
                          >
                            <CustomCheckbox
                              id={user?._id}
                              checkedProp={user?._id === selectedSponsor}
                              onChange={() => handleSelectSponsor(user?._id)}
                            />
                            <UserColumn t={t} user={user} />
                          </div>
                        ))}
                      </ScrollContainer>
                    ) : (
                      <span className="hint-form">
                        {t('selectSponsor.modal.sponsors.none')}
                      </span>
                    )}
                  </div>
                </div>
              </div>
              <div className="selectSponsorModal-footer">
                <button
                  type="button"
                  onClick={handleSubmit}
                  className={submitBtnCx}
                  disabled={!selectedSponsor || isLoading}
                >
                  {t('selectSponsor.modal.sponsors.choose')}
                </button>
              </div>
            </div>
          </div>
        </Modal>
      ) : null}
    </>
  );
}

SelectSponsorModal.propTypes = {};

export default compose(translate, withCSRF)(SelectSponsorModal);
